import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDataExchange } from './queryKeys';

type SetResponsiblePersonPayload = {
  dataExchangeId: UUID;
  responsibleUserId: UUID;
};

type SetResponsiblePersonResponse = {
  code: number;
  message: string;
  requestId: UUID | undefined;
};

/**
 * Sets a responsible person for a specified data exchange.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Exchange/operation/update_responsible_user_data_exchange_responsible_user_put

 */
export const setResponsiblePerson = async ({
  dataExchangeId,
  responsibleUserId,
}: SetResponsiblePersonPayload) => {
  const response = await vestigasApi
    .put(ENDPOINT.DATA_EXCHANGE.SET_RESPONSIBLE_PERSON(), {
      searchParams: {
        dataExchangeId,
        responsibleUserId,
      },
    })
    .json<SetResponsiblePersonResponse>();

  return response;
};

/**
 * Custom hook for setting a responsible person for a given data exchange using mutations.
 *
 * @returns {Object} An object containing the mutation function and other properties.
 */
export const useMutationSetResponsiblePerson = (
  options?: UseMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      dataExchangeId,
      responsibleUserId,
    }: SetResponsiblePersonPayload) =>
      setResponsiblePerson({
        dataExchangeId,
        responsibleUserId,
      }),
    onSuccess(_, { dataExchangeId }) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysDataExchange.getAllSuppliers(undefined),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysDataExchange.get(dataExchangeId),
      });
    },
    ...options,
  });
};
